import React, { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { AREE } from "@/data/aree";
import { ArrowLeft, CalendarDays } from "lucide-react";
import DOMPurify from "dompurify";
const stripNbsp = (s) => (s || "")
  .replace(/\u00a0/g, " ")
  .replace(/&nbsp;/gi, " ")
  .replace(/&#160;/gi, " ")
  .replace(/&#xa0;/gi, " ");
const Html = ({ html, className = "" }) => (
  <div className={className} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(stripNbsp(html)) }} />
);

export default function AreaDetail() {
  const { slug } = useParams();
  const area = AREE.find((a) => a.slug === slug);
  useEffect(() => {
    if (area) {
      const title = area.title;
      const intro = area.intro;
      document.title = `${title} — Dania (Dan) Piras`;
      const desc = document.querySelector('meta[name="description"]');
      if (desc) desc.setAttribute("content", typeof intro === "string" ? intro.replace(/<[^>]+>/g, "").slice(0, 160) : "");
    }
  }, [area]);

  if (!area) {
    return (
      <div className="container-editorial py-24" data-testid="area-not-found">
        <p className="text-viola/70 dark:text-cream/70">Ambito non trovato.</p>
        <Link to="/aree-di-competenza" className="mt-4 inline-flex items-center gap-2 text-fucsia"><ArrowLeft size={16} /> Torna agli ambiti</Link>
      </div>
    );
  }

  const title = area.title;
  const intro = area.intro;
  const body = `<p>${area.body}</p>`;

  return (
    <div data-testid={`page-area-${area.slug}`}>
      <div className={area.color}>
        <div className="container-editorial py-16 lg:py-24">
          <Link to="/aree-di-competenza" className="inline-flex items-center gap-2 text-sm text-viola/70 hover:text-viola"><ArrowLeft size={14} /> Aree di lavoro</Link>
          <p className="mt-6 text-xs font-semibold uppercase tracking-widest text-viola/60">{area.tag}</p>
          <h1 className="mt-4 font-display text-5xl sm:text-7xl tracking-tighter leading-[0.95] max-w-4xl text-viola">{title}</h1>
          <Html html={intro} className="mt-8 text-xl text-viola/80 max-w-3xl rich-body cms-para" />
          {area.tags?.length > 0 && (
            <div className="mt-8 flex flex-wrap gap-2">
              {area.tags.map((t) => (
                <span key={t} className="text-xs rounded-full bg-white/60 px-3 py-1.5 text-viola/85 font-medium">{t}</span>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="container-editorial py-16 grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2 space-y-6 text-lg leading-relaxed text-viola/85 dark:text-cream/85">
          <Html html={body} className="rich-body" />
          <h2 className="font-display text-3xl pt-6 dark:text-cream">Come lavoriamo insieme</h2>
          <ul className="list-none space-y-3 pl-0">
            <li className="flex gap-4"><span className="text-fucsia font-semibold">01</span> <span>Il primo incontro è parte integrante del percorso: la conoscenza reciproca richiede tempo. Non prevedo un colloquio conoscitivo separato.</span></li>
            <li className="flex gap-4"><span className="text-fucsia font-semibold">02</span> <span>Almeno tre incontri prima di decidere insieme se proseguire.</span></li>
            <li className="flex gap-4"><span className="text-fucsia font-semibold">03</span> <span>Percorso individuale, di coppia/diade o per configurazioni relazionali plurime. Interamente online.</span></li>
          </ul>
          <p className="pt-4 text-sm text-viola/60 dark:text-cream/60">
            Vedi la <Link to="/quando-non-sono-la-persona-giusta" className="text-fucsia font-semibold">nota di trasparenza</Link> per capire quando potrei non essere la persona giusta per te.
          </p>
        </div>
        <aside className="space-y-4">
          <div className="rounded-2xl bg-viola text-cream p-8">
            <h3 className="font-display text-2xl">Vuoi parlarne?</h3>
            <p className="mt-3 text-cream/80 text-sm">Scrivimi per un primo contatto. Se non ricevi risposta entro una settimana, puoi contattarmi su Telegram: <strong>@danpiras_psy</strong> (+39 375 614 8885 — solo Telegram).</p>
            <Link to="/consulenze" className="mt-6 btn-secondary w-full justify-center" data-testid="area-cta-consulenza"><CalendarDays size={16} /> Vai ai percorsi</Link>
          </div>
          <div className="rounded-2xl bg-lilla p-8">
            <p className="text-xs uppercase tracking-widest text-viola/60 font-semibold">Anche su Instagram</p>
            <p className="mt-2 font-display text-xl text-viola">@hello_policose</p>
            <p className="mt-2 text-sm text-viola/75">Contenuti gratuiti di divulgazione su questi temi, dal 2019.</p>
            <a href="https://www.instagram.com/hello_policose/" target="_blank" rel="noopener noreferrer" className="mt-4 inline-block text-sm font-semibold text-fucsia">Vai al profilo →</a>
          </div>
        </aside>
      </div>
    </div>
  );
}
